/**
 * EWBCI API Client メインクライアント
 */
import Log from '@/Logger';

import { sendEWBCIRequest } from '@/LantanaAPI/graphql/mutations';
import { connectEWBCI } from '@/LantanaAPI/graphql/subscriptions';

import {
    executeMutation,
    executeSubscription,
    generateRequestId,
    S3,
} from '@/feature/api-client';
import type {
    EWBCIRequestInput,
    ItpcmdRequestInput,
    FileRequestInput,
    SubscribeRequestInput,
    EWBCIResponse,
    Subscription,
} from '@/feature/api-client';

import {
    toLog,
    ConnectionStatus
} from './types';
import type {
    EWBCIClientConfig,
    EWBCIRequestMessage,
    EWBCIResponseMessage,
    EWBCIGetDGTResponse,
    EWBCIItpcmdResponse,
    EWBCIEventMessage,
    PageEventHandlers,
    EWBCISubscribeResponse,
    EWBCIChunkedMessage,
    ChangeConnectionStatusHandler,
    EWBCIGetDGTRequest,
} from './types';

import { generateSessionId, getCurrentUserId, type ConnectionResult } from './connection';
import { parseEWBCIMessage } from './message';
import { EWBCIError, EWBCIErrorType } from '../utils/errors';
import { LOG_COMPONENT } from '../utils/constants';

export type ItpcmdRequestBody = ItpcmdRequestInput;
export type FileRequestBody = FileRequestInput;
export type SubscribeRequestBody = SubscribeRequestInput;

/**
 * __typename プロパティを利用して型バリデーションを行うユーティリティ関数
 * @param obj バリデート対象のオブジェクト
 * @param typeName 期待する型名（__typename の値）
 * @returns 型が一致すれば true, そうでなければ false
 */
function checkType<T extends { __typename: string }>(
    obj: unknown,
    typeName: string
): obj is T {
    return (
        typeof obj === 'object' &&
        obj !== null &&
        '__typename' in obj &&
        (obj as any).__typename === typeName
    );
}

/**
 * EWBCI API別のレスポンス型定義
 */
export type EWBCISendRequestResult =
    | { api: 'getdgt'; result: any } // TODO: 具体的な型定義
    | { api: 'itpcmd'; result: any } // TODO: 具体的な型定義  
    | { api: 'file'; result: any } // TODO: 具体的な型定義
    | { api: 'subscribe'; result: any }; // TODO: 具体的な型定義

interface ChunkHandler {
    processChunk: (message: EWBCIChunkedMessage) => Promise<void>;
    clearAllChunks: () => void;
};

/**
 * EWBCI API Client メインクラス
 * 
 * EWBCI（eWhiteboard Command Interface）への接続、メッセージ送受信、
 * 状態管理を行うメインクライアントクラス。
 * 
 * TODO: 状態に応じた異常系が未実装　例：非CONNECTED状態でitpcmdを実行しようとした場合など
 */
export class EWBCIClient {
    private readonly logger = Log.getLogger();
    private readonly config: Required<EWBCIClientConfig>;

    // 接続状態
    private connectionStatus: ConnectionStatus = ConnectionStatus.NONE;
    private connection: ConnectionResult | null = null;
    private sessionId: string | null = null;
    private dgt: string | null = null;

    // メッセージ管理
    private readonly requestRecord = new Map<string, { sentAt: number }>();
    private readonly responseResults: EWBCIResponseMessage[] = [];

    // イベントハンドラー
    private onChangeConnectionStatus?: ChangeConnectionStatusHandler;

    // ページサブスクライブ関連の状態管理
    private subscription: {
        pageId: string;
        handlers: PageEventHandlers;
    } | null = null;

    // タイマー管理
    private keepaliveTimer: NodeJS.Timeout | null = null;

    private chunkHandler: ChunkHandler;

    constructor(config: EWBCIClientConfig, chunkHandler: ChunkHandler) {
        this.config = {
            ...config,
        };

        this.chunkHandler = chunkHandler;

        this.logger.ui.info(LOG_COMPONENT, 'constructor',
            `EWBCIClient initialized with config: ${JSON.stringify({
                serialId: this.config.serialId,
                deviceId: this.config.deviceId,
                tenantId: this.config.tenantId,
            })}`
        );
    }

    /**
     * リクエストのヘッダを取得する
     * @param requestId [Optional] リクエストID
     * @returns リクエストのヘッダ
     * @throws {EWBCIError} Assertionエラー
     */
    private requestHeader(requestId?: string): {
        sessionId: string;
        requestId: string;
        serialId: string;
        deviceId?: string;
    } {
        if (!this.sessionId) {
            throw new EWBCIError("Session ID not available", EWBCIErrorType.AssertError);
        }
        if (!this.config.serialId || !this.config.deviceId) {
            throw new EWBCIError("Target Device ID not selected", EWBCIErrorType.AssertError);
        }
        if (!requestId)
            requestId = generateRequestId();
        return {
            sessionId: this.sessionId,
            requestId,
            serialId: this.config.serialId,
            deviceId: this.config.deviceId,
        }
    }

    /**
     * 接続状態を取得
     */
    getConnectionStatus(): ConnectionStatus {
        return this.connectionStatus;
    }

    /**
     * セッションIDを取得
     */
    getSessionId(): string | null {
        return this.sessionId;
    }

    /**
     * 設定を取得
     */
    getConfig(): Readonly<Required<EWBCIClientConfig>> {
        return { ...this.config };
    }

    /**
     * イベントハンドラーを設定(/解除)
     */
    setOnChangeConnectionStatus(handler: ChangeConnectionStatusHandler | null): void {
        this.onChangeConnectionStatus = handler ?? undefined;
    }

    /**
     * EWBCI に接続
     * 
     * sendEWBCIRequestによる要求メッセージのレスポンスを受信できる状態にする。
     */
    async connect(): Promise<void> {
        try {
            if (this.connectionStatus !== ConnectionStatus.NONE &&
                this.connectionStatus !== ConnectionStatus.SERVER_DISCONNECTED) {
                this.logger.ui.warn(LOG_COMPONENT, 'connect', `Already connecting or connected. Current status: ${this.connectionStatus}`);
                return;
            }

            // 新しいセッション開始
            const userId = await getCurrentUserId();
            const sessionId = generateSessionId();

            // 状態を更新
            this.sessionId = sessionId;
            this.handleStatusChange(ConnectionStatus.SERVER_CONNECTING); // この状態遷移から新しいsessionIdが取得できるものとする

            this.logger.ui.info(LOG_COMPONENT, 'connect', `Starting connection to EWBCI with userId: ${userId}, sessionId: ${this.sessionId}`);

            // 接続試行開始
            const subscription = await executeSubscription(
                connectEWBCI,
                {
                    input: {
                        userId,
                        sessionId,
                    }
                },
                {
                    next: (data: any) => this.onSubscriptionNext(sessionId, data),
                    complete: () => this.onSubscriptionComplete(sessionId),
                    error: (error) => this.onSubscriptionError(sessionId, error),
                }
            );

            this.connection = {
                sessionId,
                unsubscribe: () => this.onSubscriptionUnsubscribe(sessionId, subscription),
            };

            // 接続成功
            this.handleStatusChange(ConnectionStatus.SERVER_CONNECTED);

            this.logger.ui.info(LOG_COMPONENT, 'connect', `Successfully connected with sessionId: ${this.sessionId}`);

        } catch (error) {
            this.logger.ui.error(LOG_COMPONENT, 'connect', `Connection failed: ${error}`);
            // エラータイプはServerConnectionErrorで固定する
            const ewbciError = new EWBCIError('Failed to connect to eWB', EWBCIErrorType.ServerConnectionError, error);
            // TODO エラータイプの細分化
            // エラーハンドリング
            this.handleError(ewbciError);
        }
    }

    /**
     * subscriptionに渡すメッセージハンドラ
     * @param sessionId subscribe時のセッションID
     * @param data メッセージ（レスポンス）
     * @throws EWBCIError エラー発生時にはMessageErrorをスローする
     */
    private onSubscriptionNext(sessionId: string, data: any): void {
        try {
            // デバイスからメッセージが来たらここが動く
            const response = data?.connectEWBCI as EWBCIResponse;

            // EWBCIResponseではない場合は無視
            if (response.__typename !== "EWBCIResponse") {
                this.logger.api.warn(LOG_COMPONENT, 'connect', `Received unexpected response type: ${response.__typename}`);
                return;
            }

            // セッションIDが一致しない場合は無視
            if (response.sessionId !== sessionId) {
                this.logger.api.debug(LOG_COMPONENT, 'connect', `Session ID mismatch: expected ${sessionId}, got ${response.sessionId}`);
                return;
            }

            // メッセージを処理
            this.handleMessage(response);

        } catch (error) {
            this.logger.ui.error(LOG_COMPONENT, 'connect', `Error handling EWBCI response: ${error}`);
            this.handleError(EWBCIError.fromError(error, EWBCIErrorType.MessageError)); // ここでエラーの伝搬を止める
        }
    }

    /**
     * subscriptionに渡す完了ハンドラ
     * @param sessionId subscribe時のセッションID
     */
    private onSubscriptionComplete(sessionId: string): void {
        // 基本的に呼ばれることはない。（Lantana側からサブスクライブを閉じる場合）
        this.logger.api.info(LOG_COMPONENT, 'connect', `Connection completed for session: ${sessionId}`);
        if (this.connectionStatus !== ConnectionStatus.DISCONNECTED) { // 切断状態以外で届いたらエラー扱い
            this.handleError(new EWBCIError(`Connection completed for session: ${sessionId}`, EWBCIErrorType.ServerConnectionError));
        }
    }

    /**
     * subscriptionに渡すエラーハンドラ
     * @param sessionId subscribe時のセッションID
     * @param error 発生したエラー
     */
    private onSubscriptionError(sessionId: string, error: any): void {
        this.logger.ui.error(LOG_COMPONENT, 'connect', `Connection failed: ${error}`);
        this.handleError(EWBCIError.fromError(error, EWBCIErrorType.ServerConnectionError));
    }

    /**
     * 外部に渡すUnsubscribe用のハンドル
     * @param sessionId subscribe時のセッションID
     * @param subscription subscribeのレスポンス（unsubscribeハンドル）
     */
    private onSubscriptionUnsubscribe(sessionId: string, subscription: Subscription): void {
        this.logger.ui.info(LOG_COMPONENT, 'connect', `Unsubscribing from EWBCI with session ID: ${sessionId}`);
        subscription.unsubscribe();
        this.handleStatusChange(ConnectionStatus.DISCONNECTED);
    }

    /**
     * eWBへ接続する
     * 
     * ※接続状態＝有効なDGTを保持していて、eWBとデータ安全な通信が可能な状態
     */
    async connectToEWB(): Promise<void> {
        this.logger.ui.info(LOG_COMPONENT, 'connectToEWB', 'Start connecting');

        // 接続状態を更新
        this.handleStatusChange(ConnectionStatus.EWB_CONNECTING);

        try {
            // DGTリフレッシュ
            this.logger.ui.debug(LOG_COMPONENT, 'connectToEWB', 'Refreshing DGT...');
            this.dgt = await this.fetchDalmatianGatewayToken();
            this.logger.ui.info(LOG_COMPONENT, 'connectToEWB', 'Connecting completed successfully');

            // 接続状態を更新
            this.handleStatusChange(ConnectionStatus.CONNECTED);

        } catch (error) {
            this.logger.ui.error(LOG_COMPONENT, 'connectToEWB', `Connecting failed: ${error}`);
            // エラータイプは発生元で決定されている
            const ewbciError = new EWBCIError('Failed to connect to eWB', EWBCIErrorType.Unknown, error);
            // エラーハンドリング
            this.handleError(ewbciError);
        }
    }

    /**
     * EWBCI から切断
     */
    disconnect(): void {
        this.logger.ui.info(LOG_COMPONENT, 'disconnect', 'Disconnecting from EWBCI');

        this.handleStatusChange(ConnectionStatus.DISCONNECTED);

        // 接続を切断
        if (this.connection) {
            this.connection.unsubscribe();
            this.connection = null;
        }

        // 状態をリセット
        this.sessionId = null;
        this.responseResults.length = 0;
        this.requestRecord.clear();
        this.chunkHandler.clearAllChunks();

        // サブスクライブ状態をリセット
        if (this.isSubscriptionActive()) {
            this.unsubscribePage();
        }
    }

    /** DalmatianGatewayTokenが有効か否か（有効ならDalmatianGateway↔eWB間通信のペイロード暗号化が有効になる） */
    isEncryptionAvailable(): boolean { return this.dgt !== null; }
    /** DalmatianGatewayToken を無効化する */
    private invalidateDalmatianGatewayToken(): void { this.dgt = null; }
    /** DalmatianGatewayToken を取得 */
    async fetchDalmatianGatewayToken(): Promise<string> {
        const head = this.requestHeader();
        // リクエストを実行
        const resp = await this.execute({
            ...head,
            getdgt: '{}',
        } as EWBCIGetDGTRequest) as EWBCIGetDGTResponse;
        // レスポンスを型チェック
        if (!checkType(resp, 'EWBCIGetDGTResponse')) {
            throw new EWBCIError('トークンの取得に失敗しました', EWBCIErrorType.AssertError);
        }
        // トークンを取り出す
        const dgt = resp.body.body?.dgt;
        if (!dgt) {
            throw new EWBCIError('トークンの取得に失敗しました', EWBCIErrorType.AssertError);
        }
        return dgt;
    }

    /**
     * itpcmdを実行し、結果を取得する
     */
    async executeItpcmd(itpcmd: ItpcmdRequestBody, requestId?: string, timeoutMs?: number): Promise<EWBCIItpcmdResponse> {
        const head = this.requestHeader(requestId);
        // リクエストを実行
        const resp = await this.execute({ ...head, itpcmd }, timeoutMs) as EWBCIItpcmdResponse;
        // レスポンスを型チェック
        if (!checkType(resp, 'EWBCIItpcmdResponse')) {
            throw new EWBCIError('itpcmdの実行に失敗しました', EWBCIErrorType.AssertError);
        }
        return resp;
    }

    /**
     * ファイル生成を伴うitpcmdを実行し、結果としてファイルを取得する
     */
    async getFileWithItpcmd(itpcmd: ItpcmdRequestBody, requestId?: string, dlIfExists: boolean = false): Promise<Blob> {
        if (!itpcmd.fileOption)
            throw new EWBCIError('fileOptionが指定されてません', EWBCIErrorType.AssertError);

        // S3におけるファイルパス
        const objectKey = `stable/device_files/${this.config.tenantId}/${this.config.serialId}/${itpcmd.fileOption.s3Suffix}`;
        // itpcmd実行前チェック(取得できればそのまま返す)
        if (dlIfExists) {
            try {
                // s3上に該当ファイルが存在するかをチェック→存在する場合はそれを使う
                return await S3.getObjectAsBlob(objectKey);
            } catch (err) {
                if (err instanceof S3.S3ClientError) {
                    if (err.status !== 404) { // not foundは正常系もある
                        throw new EWBCIError("S3オブジェクトのチェックに失敗しました。", EWBCIErrorType.Unknown, err);
                    }
                } else if (err instanceof Error) {
                    throw new EWBCIError("S3オブジェクトのチェックに失敗しました。", EWBCIErrorType.Unknown, err);
                } else {
                    throw new EWBCIError("S3オブジェクトのチェックに失敗しました。", EWBCIErrorType.Unknown);
                }
            }
        }
        // itpcmd実行
        const resp = await this.executeItpcmd(itpcmd, requestId);
        // レスポンスをチェック
        if (resp.body.status !== 200) {
            throw new EWBCIError(`itpcmdの実行に失敗しました: [${resp.body.status}] ${resp.body.reason}`, EWBCIErrorType.EwbciError);
        }
        // s3からファイルを取得
        try {
            return await S3.getObjectAsBlob(objectKey);
        } catch (err) {
            throw new EWBCIError("S3オブジェクトの取得に失敗しました。", EWBCIErrorType.ServerError, err);
        }
    }

    /**
     * eWB内部の文書に紐付いたファイルを取得する
     */
    async fetchFileInDocument(docId: string, fileName: string, requestId?: string): Promise<Blob> {
        if (!fileName) { // ファイル名が設定されていない場合は空を返す
            throw new EWBCIError('File name is not specified', EWBCIErrorType.AssertError);
        }
        const s3Suffix = `${docId}/${fileName}`;

        // s3にファイルがない場合はデバイスにアップロードを要求
        const itpcmd: ItpcmdRequestBody = {
            method: "GET",
            query: `file/${docId}/${fileName}`,
            fileOption: {
                path: "filepath", // 実際のファイルパスはデバイス側で決定される
                s3Suffix, // S3にアップロードする際のファイル名
                retain: false // デバイス上に一時ファイルを残すかどうか
            }
        };
        // リクエストを実行
        return this.getFileWithItpcmd(itpcmd, requestId, true);
    }

    /**
     * eWB内部の文書に紐付いたファイルを取得する
     */
    async getLatestDocumentPDF(docId: string, requestId?: string): Promise<Blob> {
        // 一時的にS3にアップロードする際のファイル名
        const s3Suffix = `${docId}/${Date.now()}-download.pdf`;
        // s3にファイルがない場合はデバイスにアップロードを要求
        const itpcmd: ItpcmdRequestBody = {
            method: "GET",
            query: `doc/${docId}`,
            fileOption: {
                path: "filepath", // 実際のファイルパスはデバイス側で決定される
                s3Suffix, // S3にアップロードする際のファイル名
                retain: false // デバイス上に一時ファイルを残すかどうか
            }
        };
        // リクエストを実行
        return this.getFileWithItpcmd(itpcmd, requestId, false);
    }

    private async execute(request: EWBCIRequestMessage, timeoutMs: number = 30000): Promise<EWBCIResponseMessage> {
        // リクエストを送信
        await this.sendRequest(request);
        // レスポンスを待ち受ける
        return await this.waitForResponse(request.requestId, timeoutMs);
    }

    /**
     * リクエストを送信し、レスポンスを受信します
     */
    private async sendRequest(request: EWBCIRequestMessage): Promise<void> {
        const isGetDGT = Object.keys(request ?? {}).includes('getdgt');
        if (!isGetDGT && this.connectionStatus !== ConnectionStatus.CONNECTED) { // getdgt以外のリクエストは接続状態をチェック
            throw new EWBCIError(`Cannot send request: not connected (status: ${this.connectionStatus})`, EWBCIErrorType.EwbConnectionError);
        }

        this.logger.api.info("Lantana-API", `start to sendEWBCIRequest`);

        const headers: Record<string, string> = {};
        if (this.dgt !== null && !isGetDGT) {
            headers.dgt = this.dgt; // dgtが設定されていればヘッダーに追加
        }

        const variables = {
            input: request as EWBCIRequestInput,
        };

        try {
            // 送信
            await executeMutation(sendEWBCIRequest, variables, request.requestId, headers);
        } catch (err: unknown) {
            // エラー
            if (err instanceof Error) {
                throw EWBCIError.fromError(`Failed to get devices: ${err.message}`, EWBCIErrorType.ServerConnectionError);
            } else {
                throw new EWBCIError(`Failed to get devices: Unknown error: ${JSON.stringify(err)}`, EWBCIErrorType.ServerConnectionError);
            }
            // TODO : executeMutationでthrowされたエラーを分類してEWBCIErrorに変換する
        }
    }

    private async waitForResponse(requestId: string, timeoutMs: number): Promise<EWBCIResponseMessage> {
        const intervalMs = 1000; // 1秒毎にチェック
        const start = Date.now();
        return new Promise<EWBCIResponseMessage>((resolve, reject) => {
            const timer = setInterval(async () => {
                // 排他ロックを施して結果一覧をチェック
                const found = this.responseResults.findIndex(resp => resp.requestId === requestId);
                if (found > -1) {
                    const resp = this.responseResults.at(found)!;
                    this.logger.api.info("Lantana-API", `Succeeded to receive response.${toLog(resp)}`);
                    clearInterval(timer); // タイマー解除
                    resolve(resp);
                    this.responseResults.splice(found, 1); // リストから削除
                } else if (Date.now() - start >= timeoutMs) {
                    this.logger.api.warn("Lantana-API", `Timeout for wait response: ${requestId} `);
                    clearInterval(timer); // タイマー解除
                    reject(new EWBCIError(`Timeout for wait response: ${requestId}`, EWBCIErrorType.EwbConnectionError));
                }
            }, intervalMs);
        });
    }

    /**
     * メッセージハンドラー
     */
    handleMessage(response: EWBCIResponse): void {
        try {
            this.logger.api.debug(LOG_COMPONENT, 'handleMessage',
                `Received message for session: ${response.sessionId}, request: ${response.requestId}`
            );

            // メッセージをパース
            const message = parseEWBCIMessage(response);

            this.logger.api.info(LOG_COMPONENT, "handleMessage", toLog(message));

            //リクエストしたものか、デバイスの変更によるものかなどを判別し、それぞれ別の処理キューにぶちこむ
            switch (message.__typename) {
                case 'EWBCIChunkedMessage':
                    // チャンクメッセージの場合はチャンク管理コンポーネントに委譲
                    this.handleChunkedMessage(message);
                    return;

                case 'EWBCIEventMessage':
                    // EWBCIEventMessageの場合、イベント通知する
                    this.handleSubscribeEvent(message);
                    return;

                case 'EWBCIGetDGTResponse':
                case 'EWBCIItpcmdResponse':
                case 'EWBCIFileResponse':
                case 'EWBCISubscribeResponse':
                    // EWBCIResponseMessageの場合、キューイングする
                    this.handleRequestResponse(message);
                    return;

                default:
                    this.logger.api.warn(LOG_COMPONENT, "handleEWBCIResponse", `Received response for unknown request. ${toLog(message)}`);
                    return;
            }

        } catch (error) {
            throw new EWBCIError("Failed to handleMessage", EWBCIErrorType.MessageError, error);
        }
    }

    /**
     * レスポンスを処理
     */
    private handleRequestResponse(message: EWBCIResponseMessage): void {
        this.logger.ui.debug(LOG_COMPONENT, 'handleRequestResponse', `Received response: ${message.requestId}`);
        // キューイング
        this.responseResults.push(message); // waitForResponseで拾われる
    }

    /**
     * サブスクライブイベントを処理
     */
    private handleSubscribeEvent(message: EWBCIEventMessage): void {
        if (!this.isSubscriptionActive()) {
            this.logger.ui.warn(LOG_COMPONENT, 'handleSubscribeEvent', 'Received event but no active subscription');
            return;
        }
        const { pageId, handlers } = this.subscription!;
        if (pageId !== message.body.pageId) {
            this.logger.ui.warn(LOG_COMPONENT, 'handleSubscribeEvent', `Received event but defferent page event: ${message.body.pageId}`);
            return;
        }
        const { body: { body: event }, requestedAt } = message;
        const timestamp = new Date(requestedAt);

        this.logger.ui.debug(LOG_COMPONENT, 'handleSubscribeEvent', `Received event: type=${event.type}, pageId=${pageId}`);

        // イベントタイプに応じたハンドラーを呼び出し
        switch (event.type) {
            case 'form':
                handlers.onChangePageForm?.(pageId, event.form, timestamp);
                break;
            case 'pageImage':
                handlers.onChangePageImage?.(pageId, event.file, timestamp);
                break;
            case 'timeout':
                // 接続状態を更新→状態の監視元で再接続シーケンスを開始する
                this.handleStatusChange(ConnectionStatus.EWB_DISCONNECTED);
                break;
            case 'conflict':
                this.logger.ui.warn(LOG_COMPONENT, 'handleSubscribeEvent', `Subscribe error event: ${event.type} - ${event.message}`);
                // 致命エラー：セッション競合のためSERVER_DISCONNECTED扱いする
                this.handleStatusChange(ConnectionStatus.SERVER_DISCONNECTED);
                break;
            default:
                this.logger.ui.warn(LOG_COMPONENT, 'handleSubscribeEvent', `Unknown event type: ${(event as any).type}`);
        }
    }

    /** 
     * チャンクメッセージを処理
     */
    private handleChunkedMessage(message: EWBCIChunkedMessage) {
        this.logger.ui.debug(LOG_COMPONENT, 'handleChunkedMessage', `Chunked message: ${message.requestId}'(${message.body.i + 1}/${message.body.n})`);
        // キューイング
        this.chunkHandler.processChunk(message).catch(e => {
            // awaitで待たないためここまで伝搬してきたエラーを処理する必要がある。
            this.logger.ui.error(LOG_COMPONENT, 'handleChunkedMessage', `Failed to processChunk: ${message.requestId}'(${message.body.i + 1}/${message.body.n}) : ${e}`);
        });
    }

    /**
     * 接続状態変更ハンドラー
     */
    private handleStatusChange(status: ConnectionStatus): void {
        if (this.connectionStatus === status) {
            return; // 状態が変わっていなければ何もしない
        }
        const previousStatus = this.connectionStatus;
        this.connectionStatus = status;

        // ログ出力
        this.logger.ui.info(LOG_COMPONENT, 'handleStatusChange', `Status changed: ${previousStatus} -> ${status}`);

        // 状態変更の副作用を処理
        try {
            switch (status) {
                /* LantanaAPI切断時
                 * - サブスクリプションはリセット（Pending）
                 *   - DalmatianGatewayTokenを無効化
                 * - 3秒後に再接続を試みる
                 */
                case ConnectionStatus.SERVER_DISCONNECTED:
                    // DalmatianGatewayTokenを無効化
                    this.invalidateDalmatianGatewayToken();

                    // サブスクリプションは一時停止
                    if (this.isSubscriptionActive()) {
                        this.unavailablePageSubscription();
                    }

                    // 3秒後に再接続を試みる
                    setTimeout(async () => {
                        if (this.connectionStatus !== ConnectionStatus.SERVER_DISCONNECTED) return; // 状態が変わっていたら何もしない
                        // 再接続
                        this.connect() // 状態遷移: SERVER_DISCONNECTED → SERVER_CONNECTING → SERVER_CONNECTED
                            .catch(err => {
                                const ewbciError = new EWBCIError("Uncaught Error", EWBCIErrorType.AssertError, err);
                                this.logger.ui.error(LOG_COMPONENT, 'onStatusChange', `Reconnection attempt failed: ${ewbciError}`);
                            });
                    }, 3000);
                    break;

                case ConnectionStatus.SERVER_CONNECTING:
                    break;
                case ConnectionStatus.SERVER_CONNECTED:
                    // eWB接続を開始
                    this.connectToEWB() // 状態遷移: SERVER_CONNECTED → EWB_CONNECTING → CONNECTED
                        .catch(err => {
                            const ewbciError = new EWBCIError("Uncaught Error", EWBCIErrorType.AssertError, err);
                            this.logger.ui.error(LOG_COMPONENT, 'onStatusChange', `eWB Connection attempt failed: ${ewbciError}`);
                        });
                    break;

                /* eWB切断時
                 * - サブスクリプションはリセット（Pending）
                 *   - DalmatianGatewayTokenを無効化
                 * - 3秒後に再接続を試みる
                 */
                case ConnectionStatus.EWB_DISCONNECTED:
                    // DalmatianGatewayTokenを無効化
                    this.invalidateDalmatianGatewayToken();

                    // サブスクリプションは一時停止
                    if (this.isSubscriptionActive()) {
                        this.unavailablePageSubscription();
                    }

                    // 3秒後に再接続を試みる
                    setTimeout(async () => {
                        if (this.connectionStatus !== ConnectionStatus.EWB_DISCONNECTED) return; // 状態が変わっていたら何もしない
                        this.connectToEWB() // 状態遷移: EWB_DISCONNECTED → EWB_CONNECTING → CONNECTED
                            .catch(err => {
                                const ewbciError = new EWBCIError("Uncaught Error", EWBCIErrorType.AssertError, err);
                                this.logger.ui.error(LOG_COMPONENT, 'onStatusChange', `eWB Reconnection attempt failed: ${ewbciError}`);
                            });
                    }, 3000);
                    break;

                case ConnectionStatus.EWB_CONNECTING:
                    break;

                case ConnectionStatus.CONNECTED:
                    // 接続完了したらサブスクライブを開始/再開
                    if (this.isSubscriptionActive()) {
                        const { pageId, handlers } = this.subscription!;
                        // サブスクライブを開始/再開
                        this.subscribePage(pageId, handlers, true) // reconnect=true
                            .catch(err => {
                                if (err instanceof EWBCIError) {
                                    // エラーハンドリング
                                    this.handleError(err);
                                } else {
                                    // その他のエラーはAssertErrorとして通知する
                                    this.handleError(new EWBCIError("Unhandled Error on subsscribePage", EWBCIErrorType.AssertError, err));
                                }
                            });
                    }
                    break;

                case ConnectionStatus.DISCONNECTED:
                    this.logger.ui.info(LOG_COMPONENT, 'handleStatusChange', 'Client is now fully disconnected.');
                    break;
            }
        } catch (error) {
            this.handleError(new EWBCIError("Unhandled Error on handleStatusChange", EWBCIErrorType.AssertError, error));
        } finally {
            // 登録されているハンドラーを呼び出す
            this.onChangeConnectionStatus?.(status);
        }
    }

    /** 
     * エラーハンドラー
     */
    private handleError(error: EWBCIError): void {
        this.logger.ui.error(LOG_COMPONENT, 'onError', `${error.getDetailedMessage()}`);
        try {
            // TODO エラーハンドリングは見直したい
            switch (error.errorType) {
                case EWBCIErrorType.ServerConnectionError:
                    if (this.connectionStatus === ConnectionStatus.DISCONNECTED) {
                        // DISCONNECTED状態でserver接続エラーが来た場合は無視する
                    } else {
                        // server接続エラーの場合は接続状態を更新→状態更新イベントで再接続シーケンスを開始する
                        this.handleStatusChange(ConnectionStatus.SERVER_DISCONNECTED);
                    }
                    break;
                case EWBCIErrorType.EwbConnectionError:
                    if (this.connectionStatus === ConnectionStatus.DISCONNECTED) {
                        // DISCONNECTED状態でserver接続エラーが来た場合は無視する
                    } else if (this.connectionStatus === ConnectionStatus.CONNECTED ||
                        this.connectionStatus === ConnectionStatus.EWB_CONNECTING) {
                        // eWB接続エラーの場合は接続状態を更新→状態更新イベントでeWB再接続シーケンスを開始する
                        this.handleStatusChange(ConnectionStatus.EWB_DISCONNECTED);
                    } else {
                        this.logger.ui.warn(LOG_COMPONENT, 'onError', `EwbConnectionError received but current status is ${this.connectionStatus}`);
                    }
                    break;
                case EWBCIErrorType.EwbciError:
                case EWBCIErrorType.MessageError:
                case EWBCIErrorType.AssertError:
                case EWBCIErrorType.ServerError:
                case EWBCIErrorType.Unknown:
                default:
                    // その他のエラー発生元にロギングを任せ、副作用なしとする
                    break;
            }
        } catch (e) {
            // これより上にErrorを伝搬させない
            this.logger.ui.error(LOG_COMPONENT, 'onError', `Unhandled error on handleError: ${typeof (e) === 'object' ? JSON.stringify(e) : String(e)}`);
        }
    }

    // =========================================
    // ページサブスクライブ関連
    // =========================================

    private isSubscriptionActive(): boolean {
        return !!this.subscription?.pageId;
    }

    /**
     * ページの更新検知を開始
     * @throws {EWBCIError} < ServerConnectionError | EwbConnectionError  | AsserError >
     */
    async subscribePage(pageId: string, handlers: PageEventHandlers, reconnect?: boolean): Promise<void> {
        this.logger.ui.info(LOG_COMPONENT, 'subscribePage', `Starting page subscription for pageId: ${pageId}`);

        // 既存のサブスクライブを停止
        if (!reconnect && this.isSubscriptionActive()) {
            this.logger.ui.info(LOG_COMPONENT, 'subscribePage', `Stopping previous subscription for pageId: ${this.subscription!.pageId}`);
            await this.unsubscribePage();
        }

        // 状態を更新
        this.subscription = { pageId, handlers };

        // eWB接続が確立していない場合は予約して抜ける
        if (this.connectionStatus !== ConnectionStatus.CONNECTED) {
            this.logger.ui.info(LOG_COMPONENT, 'subscribePage', `eWB not connected, subscription will be activated upon connection.`);
            return;
        }

        // サブスクライブ開始イベントを通知
        handlers.handleSubscriptionEvent?.({ type: 'SUBSCRIBING', pageId });

        // サブスクライブ実行
        const { body: { status, reason } } = await this.requestPageSubscribe(pageId, true);

        // 結果をログ出力
        this.logger.ui.debug(LOG_COMPONENT, 'subscribePage', `[${status}] ${reason}`);

        // エラーチェック
        if (status !== 200) {
            throw new EWBCIError(`Subscribe failed: [${status}] ${reason}`, EWBCIErrorType.AssertError);
        }

        // サブスクライブ成功イベントを通知
        handlers.handleSubscriptionEvent?.({ type: 'SUBSCRIBED', pageId });

        // keepaliveタイマーを開始
        this.startKeepaliveTimer();
    }

    private unavailablePageSubscription(): void {
        if (!this.isSubscriptionActive()) {
            this.logger.ui.warn(LOG_COMPONENT, 'unavailablePageSubscription', 'No active subscription to mark as unavailable');
            return;
        }
        const { pageId, handlers } = this.subscription!;

        this.logger.ui.info(LOG_COMPONENT, 'unavailablePageSubscription', `Marking subscription as unavailable for pageId: ${pageId}`);

        // keepaliveタイマーを停止
        this.stopKeepaliveTimer();

        // サブスクライブ中断イベントを通知
        handlers.handleSubscriptionEvent?.({ type: 'UNAVAILABLE', pageId });
    }

    /**
     * keepaliveタイマーを開始
     */
    private startKeepaliveTimer(): void {
        this.stopKeepaliveTimer();
        this.keepaliveTimer = setInterval(() => {
            this.sendKeepaliveSubscribe();
        }, 30000); // 30秒間隔

        this.logger.ui.debug(LOG_COMPONENT, 'startKeepaliveTimer', 'Keepalive timer started (30s interval)');
    }

    /**
     * keepaliveタイマーを停止
     */
    private stopKeepaliveTimer(): void {
        if (this.keepaliveTimer) {
            clearInterval(this.keepaliveTimer);
            this.keepaliveTimer = null;
            this.logger.ui.debug(LOG_COMPONENT, 'stopKeepaliveTimer', 'Keepalive timer stopped');
        }
    }

    /**
     * keepalive用サブスクライブリクエストを送信
     */
    private async sendKeepaliveSubscribe(): Promise<void> {
        if (!this.isSubscriptionActive()) {
            this.logger.ui.warn(LOG_COMPONENT, 'sendKeepaliveSubscribe', 'No active subscription for keepalive');
            return;
        }

        const { pageId, handlers } = this.subscription!;
        try {
            // サブスクライブ実行
            const { body: { status, reason } } = await this.requestPageSubscribe(pageId, false);

            // 結果をログ出力
            this.logger.ui.debug(LOG_COMPONENT, 'sendKeepaliveSubscribe', `[${status}] ${reason}`);

            // エラーチェック
            if (status !== 200) {
                throw new EWBCIError(`Subscribe failed: [${status}] ${reason}`, EWBCIErrorType.EwbciError); // TODO
            }

        } catch (error) {
            // eWBとの接続が切れた
            this.logger.ui.error(LOG_COMPONENT, 'sendKeepaliveSubscribe', `Keepalive failed: ${error}`);
            // タイマーを停止
            this.stopKeepaliveTimer();
            // タイムアウトイベントを通知
            handlers.handleSubscriptionEvent?.({ type: 'UNAVAILABLE', pageId });
            // 統合エラーハンドリングへ委譲
            if (error instanceof EWBCIError) {
                this.handleError(error);
            } else {
                this.handleError(new EWBCIError('Keepalive failed', EWBCIErrorType.Unknown, error as Error));
            }
        }
    }

    /** サブスクライブリクエストを実行（内部用） */
    private async requestPageSubscribe(pageId: string, renew: boolean): Promise<EWBCISubscribeResponse> {
        // ヘッダ
        const head = this.requestHeader();
        // ボディ
        const subscribe = { pageId, watch: "form,pageImage", renew };
        // リクエストを送信し、レスポンスを待つ
        const resp = await this.execute({ ...head, subscribe }, 30000); // 30秒タイムアウト

        // レスポンスを型チェック
        if (!checkType(resp, 'EWBCISubscribeResponse')) {
            throw new EWBCIError('サブスクライブ要求に失敗しました', EWBCIErrorType.AssertError);
        }

        return resp as EWBCISubscribeResponse;
    }

    /**
     * ページの更新検知を停止
     */
    async unsubscribePage(): Promise<void> {
        if (!this.isSubscriptionActive()) {
            this.logger.ui.debug(LOG_COMPONENT, 'unsubscribePage', 'No active subscription to stop');
            return;
        }
        const { pageId, handlers } = this.subscription!;
        this.logger.ui.info(LOG_COMPONENT, 'unsubscribePage', `Stopping page subscription for pageId: ${pageId}`);

        // タイマーを停止
        this.stopKeepaliveTimer();

        // サブスクライブ解除イベントを通知
        handlers.handleSubscriptionEvent?.({ type: 'UNSUBSCRIBED', pageId });

        // 状態をリセット
        this.subscription = null;
    }
}
